import type { InputPrimaryMode } from '@shared/ui/fields'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { useDisciplineScheduleStore } from './schedule/store'
import { useDisciplineScheduleSetupLessonsApi } from '../api/schedule-setup-lessons.hook'
import { useDisciplineScheduleContext } from './discipline-schedule-context.hook'

interface ModalDisciplineSetupLessonsReturn {
  refCount: Ref<string | number>
  refUnscheduled: ComputedRef<number>
  refCountMode: ComputedRef<InputPrimaryMode>
  refCountError: ComputedRef<string>
  refStartDate: Ref<string>
  refMinDate: ComputedRef<string>
  refIsDisabled: ComputedRef<boolean>
  refIsSaving: ComputedRef<boolean>
  setCount: (value: string | number) => void
  setStartDate: (value: string) => void
  close: () => void
  confirm: () => Promise<void>
}

// codestyle-ignore: date-format-enum — day-first date in the setup toast; DateFormat has no day-month-year member (decided 2026-10-29)
const TOAST_DATE_FORMAT = 'DD MMM, YYYY'

/** Books unscheduled lessons on the weekly schedule, e.g. after a top-up; at most the unscheduled count. */
export const useModalDisciplineSetupLessons = (): ModalDisciplineSetupLessonsReturn => {
  const dayjs = useDayjs()
  const store = useDisciplineScheduleStore()
  const { refDetails, refDisciplineId, customerNow, close, refreshDiscipline } = useDisciplineScheduleContext()

  const refCount = ref<string | number>(0)
  const refStartDate = ref<string>('')

  const refCountNumber = computed<number>(() => Number(refCount.value) || 0)
  const refUnscheduled = computed<number>(() => refDetails.value?.unscheduled_bookings ?? 0)
  const refHasCountError = computed<boolean>(() => refCountNumber.value > refUnscheduled.value)
  const refCountMode = computed<InputPrimaryMode>(() => (refHasCountError.value ? 'error' : 'normal'))
  const refCountError = computed<string>(() => (refHasCountError.value ? 'Enter valid value' : ''))
  const refMinDate = computed<string>(() => customerNow().format(DateFormat.DefaultDate))

  const refIsDisabled = computed<boolean>(() =>
    refCountNumber.value <= 0 || refHasCountError.value || !refStartDate.value)

  const refIsSaving = computed<boolean>(() => store.isSaving)

  const setCount = (value: string | number): void => {
    refCount.value = value
  }

  const setStartDate = (value: string): void => {
    refStartDate.value = value
  }

  const confirm = async (): Promise<void> => {
    if (refIsDisabled.value || store.isSaving) return

    const count = refCountNumber.value
    const startDate = refStartDate.value

    store.setSaving(true)

    const isDone = await useDisciplineScheduleSetupLessonsApi({
      disciplineId: refDisciplineId.value,
      payload: { bookings_count: count, start_date: startDate },
      successMessage: `${count} lessons scheduled from ${dayjs(startDate).format(TOAST_DATE_FORMAT)}`,
    })

    if (isDone) await refreshDiscipline()

    store.setSaving(false)
    if (isDone) store.closeFlow()
  }

  return {
    refCount,
    refUnscheduled,
    refCountMode,
    refCountError,
    refStartDate,
    refMinDate,
    refIsDisabled,
    refIsSaving,
    setCount,
    setStartDate,
    close,
    confirm,
  }
}
